import { useRef, useEffect, useState } from "react";
import type { DialogLine as DialogLineType, Speaker } from "@/types/dialog";
import { getLanguageConfig } from "@/lib/language-config";
import { useDialogStore } from "./dialog-store";
import { useLineAvatar } from "./use-line-avatar";

// Strips combining diacritics (general, Arabic harakat, Hebrew niqqud) without touching base letters
const DIACRITICS_RE = /[̀-ͯؐ-ًؚ-ٰٟ֑-ׇ]/g;

// CJK Unified Ideographs, Hiragana, Katakana, Hangul, Thai
const CJK_THAI_RE = /[　-鿿가-힯豈-﫿฀-๿]/;
const CONTEXT_CHARS = 8;

function normalizeLang(code: string): string {
  const raw = code.split("_")[0]!.toLowerCase();
  if (raw === "nor") return "nob";
  return raw;
}

function WordWrappedText({ text, languageCode }: { text: string; languageCode: string }) {
  const lang = normalizeLang(languageCode);
  const isCharLevel = CJK_THAI_RE.test(text);

  if (isCharLevel) {
    const chars = Array.from(text);
    return (
      <>
        {chars.map((char, i) => {
          if (/\s/.test(char)) return <span key={i}>{char}</span>;
          const context = chars.slice(i, i + CONTEXT_CHARS).join("");
          return (
            <span
              key={i}
              data-glossika-word={context}
              data-glossika-lang={lang}
              className="cursor-pointer hover:text-glossika-400 transition-colors"
            >
              {char}
            </span>
          );
        })}
      </>
    );
  }

  const tokens = text.split(/(\s+)/);

  return (
    <>
      {tokens.map((token, i) => {
        if (/^\s+$/.test(token)) return <span key={i}>{token}</span>;
        return (
          <span
            key={i}
            data-glossika-word={token}
            data-glossika-lang={lang}
            className="cursor-pointer hover:text-glossika-400 transition-colors"
          >
            {token}
          </span>
        );
      })}
    </>
  );
}

interface DialogLineItemProps {
  line: DialogLineType;
  speaker: Speaker | undefined;
  speakerThumbnailUrl: string | null;
  dialogId: string;
  languageCode: string;
  isActive: boolean;
  shouldScrollTo: boolean;
}

export function DialogLineItem({
  line,
  speaker,
  speakerThumbnailUrl,
  dialogId,
  languageCode,
  isActive,
  shouldScrollTo,
}: DialogLineItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const toggles = useDialogStore((s) => s.toggles);
  const [imgError, setImgError] = useState(false);
  const avatarUrl = useLineAvatar(
    dialogId,
    languageCode,
    line.speakerIndex,
    line.emotion || "neutral",
    speaker?.avatarBase ?? null,
  );
  const imgSrc = speakerThumbnailUrl ?? avatarUrl;
  const setActiveLine = useDialogStore((s) => s.setActiveLine);
  const audio = useDialogStore((s) => s.audio);
  const setAudio = useDialogStore((s) => s.setAudio);
  const textSizeLevel = useDialogStore((s) => s.textSizeLevel);
  const config = getLanguageConfig(languageCode);

  const isPlayingAll = useDialogStore((s) => s.audio.isPlayingAll);
  const currentLineIndex = useDialogStore((s) => s.audio.currentLineIndex);

  useEffect(() => {
    if (shouldScrollTo && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const targetY = rect.top + window.scrollY - window.innerHeight / 3;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }
  }, [shouldScrollTo]);

  useEffect(() => {
    if (isPlayingAll && currentLineIndex === line.index && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const targetY = rect.top + window.scrollY - window.innerHeight / 2 + rect.height / 2;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }
  }, [isPlayingAll, currentLineIndex, line.index]);

  const speakerColor = speaker?.gender === "male"
    ? "text-th-speaker-m"
    : speaker?.gender === "female"
      ? "text-th-speaker-f"
      : "text-th-text-2";

  let fontClass = "";
  if (config.script === "arabic") fontClass = toggles.nastaliq && config.hasNastaliq ? "font-nastaliq" : "font-arabic";
  else if (config.script === "hebrew") fontClass = "font-hebrew";
  else if (config.script === "cyrillic") fontClass = "font-cyrillic";

  const textSizeSteps = [-4, -2, 0, 2, 4, 6, 8];
  const sizeAdj = textSizeSteps[Math.max(0, Math.min(textSizeLevel + 2, textSizeSteps.length - 1))] ?? 0;
  const baseSize = config.script === "arabic" ? 20 : 16;
  const textStyle: React.CSSProperties = sizeAdj !== 0 ? { fontSize: `${baseSize + sizeAdj}px` } : {};
  const romanStyle: React.CSSProperties = sizeAdj !== 0 ? { fontSize: `${13 + sizeAdj}px` } : {};

  const displayText = !toggles.vocalization && config.hasVocalization
    ? line.text.replace(DIACRITICS_RE, "")
    : line.text;

  function handlePlayLine() {
    setActiveLine(line.index);
    if (!line.audioUrl) return;

    setAudio({ loadingLine: line.index });
    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (!el) return;

    el.src = line.audioUrl;
    el.playbackRate = audio.playbackSpeed;
    el.onended = () => {
      setAudio({ loadingLine: null });
      if (audio.repeatMode === "line") {
        el.currentTime = 0;
        el.play().catch(() => {});
      }
    };
    el.play()
      .then(() => setAudio({ loadingLine: null }))
      .catch(() => setAudio({ loadingLine: null }));
  }

  const isLoading = audio.loadingLine === line.index;

  return (
    <div
      ref={ref}
      id={`line-${String(line.index)}`}
      className={`flex gap-3 p-4 mb-2 rounded-lg border transition-colors ${
        isActive
          ? "bg-th-surface border-glossika-500/50 shadow-md shadow-glossika-500/10"
          : "bg-th-surface-dim border-th-border-dim hover:bg-th-surface"
      }`}
    >
      <button
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs transition-colors overflow-hidden ${
          isLoading
            ? "ring-2 ring-amber-500/60"
            : isActive
              ? "ring-2 ring-glossika-500/60"
              : "ring-1 ring-th-border hover:ring-glossika-500/40"
        }`}
        style={isLoading ? { animation: "pulse 1s infinite" } : undefined}
        onClick={handlePlayLine}
      >
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={speaker?.name ?? ""}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className={`w-full h-full flex items-center justify-center ${
            speaker?.gender === "male" ? "bg-blue-900/40 text-blue-400" : "bg-pink-900/40 text-pink-400"
          }`}>
            {(speaker?.name ?? "?")[0]}
          </span>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${speakerColor}`} translate="no">
          {speaker?.name ?? line.speaker}
        </div>
        <div
          className={fontClass}
          dir={config.direction === "rtl" ? "rtl" : undefined}
          style={{
            ...textStyle,
            ...(config.direction === "rtl" ? { textAlign: "center" } : {}),
          }}
        >
          <WordWrappedText text={displayText} languageCode={languageCode} />
        </div>
        {line.romanization && (
          <div
            className={`text-sm text-th-text-2 mt-1 italic ${toggles.transcription ? "" : "hidden"}`}
            style={{
              ...romanStyle,
              ...(config.direction === "rtl" ? { textAlign: "center" } : {}),
            }}
            translate="no"
          >
            {line.romanization}
          </div>
        )}
        {toggles.translation && (line.translation.en || line.translation.english || Object.values(line.translation)[0]) && (
          <div className="text-sm text-th-text-3 mt-1">
            {line.translation.en || line.translation.english || Object.values(line.translation)[0]}
          </div>
        )}
      </div>
    </div>
  );
}
